import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createAxiosInstance } from '../utils/Axios';
import { toast } from 'react-toastify';
import { useAuth, hasModuleAccess, hasSectionAccess } from '../utils/hooks/useAuth';

export default function CrmContacts() {
  const { access, loading: authLoading } = useAuth();

  // Check module and section permissions
  const hasCrmAccess = access ? hasModuleAccess('iurm', access) : false;
  const canViewContacts = access ? hasSectionAccess('iurm', 'Contacts', 'view', access) : false;
  const canCreateContact = access ? hasSectionAccess('iurm', 'Contacts', 'create', access) : false;
  const canEditContact = access ? hasSectionAccess('iurm', 'Contacts', 'edit', access) : false;
  const canDeleteContact = access ? hasSectionAccess('iurm', 'Contacts', 'delete', access) : false;

  // Check other CRM section permissions for tabs
  const canViewOverview = access ? hasSectionAccess('iurm', 'Overview', 'view', access) : false;
  const canViewLeads = access ? hasSectionAccess('iurm', 'Leads', 'view', access) : false;
  const canViewAccounts = access ? hasSectionAccess('iurm', 'Accounts', 'view', access) : false;
  const canViewTasks = access ? hasSectionAccess('iurm', 'Tasks', 'view', access) : false;
  const canViewCampaigns = access ? hasSectionAccess('iurm', 'Campaigns', 'view', access) : false;
  const canViewIntegration = access ? hasSectionAccess('iurm', 'Integration', 'view', access) : false;

  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ totalContacts: 0, customers: 0, prospects: 0, vendors: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedType, setSelectedType] = useState('');
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedLastActivity, setSelectedLastActivity] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Modal States
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', company: '', role: '', status: 'Lead', location: '' });
  const [isSavingContact, setIsSavingContact] = useState(false);

  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editContactData, setEditContactData] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
  const [deleteContactId, setDeleteContactId] = useState(null);
  const [isDeletingContact, setIsDeletingContact] = useState(false);

  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    console.log('CRM Contacts Permissions:', {
      hasCrmAccess,
      canViewContacts,
      canCreateContact,
      canEditContact,
      canDeleteContact
    });
  }, [hasCrmAccess, canViewContacts, canCreateContact, canEditContact, canDeleteContact]);

  useEffect(() => {
    const fetchContacts = async () => {
      setIsLoading(true);
      try {
        const axiosInstance = createAxiosInstance();
        const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
        
        let queryParams = `?limit=100`;
        if (searchTerm) queryParams += `&search=${searchTerm}`;
        if (selectedType) queryParams += `&type=${selectedType}`;
        if (selectedCommunity) queryParams += `&community=${selectedCommunity}`;
        if (selectedStatus) queryParams += `&status=${selectedStatus}`;
        if (selectedLastActivity) queryParams += `&lastActivity=${selectedLastActivity}`;

        const { data } = await axiosInstance.get(`${apiUrl}/contacts${queryParams}`);
        if (data && data.success) {
          setContacts(data.contacts || []);
          if (data.stats) setStats(data.stats);
          if (data.contacts && data.contacts.length > 0 && !selectedContact) {
             setSelectedContact(data.contacts[0]);
          } else if (data.contacts && data.contacts.length === 0) {
             setSelectedContact(null);
          }
        }
      } catch (error) {
        console.error("Failed to fetch contacts", error);
        toast.error("Failed to load contacts");
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchContacts();
    }, 300);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, refreshTrigger, selectedType, selectedCommunity, selectedStatus, selectedLastActivity]);

  const handleSaveContact = async () => {
    if (!newContact.name || !newContact.email) {
      toast.error("Name and Email are required");
      return;
    }
    setIsSavingContact(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.post(`${apiUrl}/contacts`, newContact);
      
      toast.success("Contact created successfully!");
      setShowAddContactModal(false);
      setNewContact({ name: '', email: '', phone: '', company: '', role: '', status: 'Lead', location: '' });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Save failed", error);
      toast.error(error?.response?.data?.message || "Failed to create contact");
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleUpdateContact = async () => {
    if (!editContactData.name || !editContactData.email) {
      toast.error("Name and Email are required");
      return;
    }
    setIsSavingEdit(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.put(`${apiUrl}/contacts/${editContactData._id}`, editContactData);
      
      toast.success("Contact updated successfully!");
      setShowEditContactModal(false);
      if (selectedContact?._id === editContactData._id) {
        setSelectedContact(editContactData);
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Update failed", error);
      toast.error(error?.response?.data?.message || "Failed to update contact");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!deleteContactId) return;
    setIsDeletingContact(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.delete(`${apiUrl}/contacts/${deleteContactId}`);
      
      toast.success("Contact deleted successfully!");
      setShowDeleteContactModal(false);
      if (selectedContact?._id === deleteContactId) {
        setSelectedContact(null);
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Delete failed", error);
      toast.error(error?.response?.data?.message || "Failed to delete contact");
    } finally {
      setIsDeletingContact(false);
      setDeleteContactId(null);
    }
  };

  // Helper to get initials
  const getInitials = (name) => {
    if (!name) return 'UN';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  // Helper to generate dynamic colors based on status or role
  const getAvatarColors = (status) => {
    switch(status?.toLowerCase()) {
      case 'customer': return { bg: '#D1FAE5', color: '#10B981' };
      case 'prospect': return { bg: '#FEF3C7', color: '#F59E0B' };
      case 'vendor': return { bg: '#DBEAFE', color: '#3B82F6' };
      default: return { bg: '#EDE9FE', color: '#7C3AED' };
    }
  };

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
        Loading permissions...
      </div>
    );
  }

  // Show access denied if no module access or no contacts view access
  if (!hasCrmAccess || !canViewContacts) {
    return (
      <div style={{ 
        padding: '60px 40px', 
        textAlign: 'center',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        margin: '20px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <h2 style={{ color: '#111827', marginBottom: '12px' }}>Access Denied</h2>
        <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '8px' }}>
          You don't have permission to access the Contacts section.
        </p>
        <p style={{ color: '#9CA3AF', fontSize: '14px' }}>
          Please contact your administrator to request access.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', backgroundColor: '#F9FAFB', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
      
      {/* Tabs Section */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #E5E7EB', marginBottom: '20px', paddingBottom: '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {canViewOverview && (
            <Link to="/crm/overview" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
                Overview
              </div>
            </Link>
          )}
          {canViewLeads && (
            <Link to="/crm/leads" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                Leads
              </div>
            </Link>
          )}
          <div className="nav-item active" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px', backgroundColor: '#8B5CF6', color: '#fff' }}>
            <div className="nav-dot" style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'currentColor' }}></div>
            Contacts
          </div>
          {canViewAccounts && (
            <Link to="/crm/accounts" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>
                Accounts
              </div>
            </Link>
          )}
          {canViewTasks && (
            <Link to="/crm/tasks" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
                Tasks
              </div>
            </Link>
          )}
          {canViewCampaigns && (
            <Link to="/crm/campaigns" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                Campaigns
              </div>
            </Link>
          )}
        </div>
      </div>

      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7C3AED' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
          </div>
          <div>
            <h1 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 4px 0', color: '#111827' }}>Contacts</h1>
            <p style={{ margin: 0, color: '#6B7280', fontSize: '12px' }}>Manage your contacts and relationships</p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #D1D5DB', borderRadius: '8px', color: '#374151', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
            Export
          </button>
          {canCreateContact && (
            <button onClick={() => setShowAddContactModal(true)} style={{ padding: '8px 16px', backgroundColor: '#5B21B6', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              Add Contact
            </button>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        {[
          { title: 'Total Contacts', value: stats.totalContacts || 0, change: '', iconColor: '#7C3AED', iconBg: '#EDE9FE', icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></> },
          { title: 'Members', value: stats.customers || 0, change: '', iconColor: '#10B981', iconBg: '#D1FAE5', icon: <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><polyline points="17 11 19 13 23 9"></polyline></> },
          { title: 'Partners', value: stats.prospects || 0, change: '', iconColor: '#F59E0B', iconBg: '#FEF3C7', icon: <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></> },
          { title: 'Mentors', value: stats.vendors || 0, change: '', iconColor: '#3B82F6', iconBg: '#DBEAFE', icon: <><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></> },
        ].map((stat, i) => (
          <div key={i} style={{ flex: 1, backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #F3F4F6', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: stat.iconBg, color: stat.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{stat.icon}</svg>
              </div>
              <span style={{ color: '#6B7280', fontSize: '12px', fontWeight: '600' }}>{stat.title}</span>
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827', marginBottom: '8px', letterSpacing: '-0.5px' }}>{stat.value}</div>
            <div style={{ fontSize: '11px', color: '#10B981', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '500' }}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>
                {stat.change || '+ 0%'}
              </span>
              <span style={{ color: '#9CA3AF' }}>vs last month</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Split */}
      <div style={{ display: 'flex', gap: '24px' }}>
        
        {/* Left Side: Table Area */}
        <div style={{ flex: '1', minWidth: 0, backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #F3F4F6', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' }}>
          
          <div style={{ display: 'flex', gap: '32px', borderBottom: '1px solid #F3F4F6', padding: '0 24px' }}>
            {['All Contacts', 'Customers', 'Prospects', 'Vendors', 'Partners'].map((tab, i) => (
              <div key={i} style={{ padding: '16px 0', borderBottom: i === 0 ? '2px solid #7C3AED' : '2px solid transparent', color: i === 0 ? '#7C3AED' : '#6B7280', fontWeight: i === 0 ? '600' : '500', fontSize: '14px', cursor: 'pointer' }}>
                {tab}
              </div>
            ))}
          </div>

          <div className="crm-table-filters" style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto' }}>
            <div style={{ position: 'relative', flex: '1 1 120px', minWidth: '120px' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', display: 'flex' }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search contacts by name, email or ph..."
                style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '12px', color: '#111827', boxSizing: 'border-box' }}
              />
            </div>

            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '12px', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', flexShrink: 0, width: '120px', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
            >
              <option value="">Type</option>
              <option value="B2B">B2B</option>
              <option value="B2C">B2C</option>
            </select>
            <select
              value={selectedCommunity}
              onChange={(e) => setSelectedCommunity(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '12px', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', flexShrink: 0, width: '120px', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
            >
              <option value="">All Community</option>
            </select>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '12px', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', flexShrink: 0 }}
            >
              <option value="">Status</option>
              <option value="Lead">Lead</option>
              <option value="Prospect">Prospect</option>
              <option value="Customer">Customer</option>
              <option value="Vendor">Vendor</option>
            </select>
            <select
              value={selectedLastActivity}
              onChange={(e) => setSelectedLastActivity(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '12px', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', flexShrink: 0 }}
            >
              <option value="">Last Activity</option>
              <option value="Today">Today</option>
              <option value="Yesterday">Yesterday</option>
              <option value="This Week">This Week</option>
            </select>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <button onClick={() => {
                setSearchTerm('');
                setSelectedType('');
                setSelectedCommunity('');
                setSelectedStatus('');
                setSelectedLastActivity('');
                setRefreshTrigger(prev => prev + 1);
                setIsRefreshing(true);
                setTimeout(() => setIsRefreshing(false), 500);
                toast.success('Filters reset and data refreshed');
              }} style={{ padding: '7px', backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#6B7280', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ transform: isRefreshing ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.5s ease' }}><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto', minHeight: '300px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px', whiteSpace: 'nowrap' }}>
              <thead>
                <tr style={{ color: '#6B7280', borderBottom: '1px solid #F3F4F6' }}>
                  <th style={{ padding: '12px 16px', fontWeight: '500' }}>ID</th>
                  <th style={{ padding: '12px 16px', fontWeight: '500' }}>Contact Name</th>
                  <th style={{ padding: '12px 16px', fontWeight: '500' }}>Email / Phone</th>
                  <th style={{ padding: '12px 16px', fontWeight: '500' }}>Role</th>
                  <th style={{ padding: '12px 16px', fontWeight: '500' }}>Community</th>
                  <th style={{ padding: '12px 16px', fontWeight: '500' }}>Last Activity</th>
                  <th style={{ padding: '12px 16px', fontWeight: '500' }}>Status</th>
                  <th style={{ padding: '12px 16px', fontWeight: '500', width: '40px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '32px', color: '#6B7280' }}>Loading contacts...</td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '32px', color: '#6B7280' }}>No contacts found.</td>
                  </tr>
                ) : (
                  contacts.map((contact, i) => {
                    const initials = getInitials(contact.name);
                    const { bg, color } = getAvatarColors(contact.status);
                    
                    return (
                      <tr key={contact._id || i} style={{ borderBottom: '1px solid #F3F4F6', color: '#111827', cursor: 'pointer', backgroundColor: selectedContact?._id === contact._id ? '#F9FAFB' : 'transparent' }}>
                        <td style={{ padding: '12px 16px', color: '#4B5563' }} onClick={() => setSelectedContact(contact)}>
                          #{contact._id ? contact._id.substring(contact._id.length - 6).toUpperCase() : '000000'}
                        </td>
                        <td style={{ padding: '12px 16px' }} onClick={() => setSelectedContact(contact)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: bg, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '11px' }}>
                              {initials}
                            </div>
                            <span style={{ fontWeight: '500' }}>{contact.name || '-'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#4B5563' }} onClick={() => setSelectedContact(contact)}>
                          <div>{contact.email || '-'}</div>
                          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{contact.phone || '-'}</div>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#4B5563' }} onClick={() => setSelectedContact(contact)}>{contact.role || '-'}</td>
                        <td style={{ padding: '12px 16px', color: '#4B5563' }} onClick={() => setSelectedContact(contact)}>{contact.community || '-'}</td>
                        <td style={{ padding: '12px 16px', color: '#4B5563' }} onClick={() => setSelectedContact(contact)}>{contact.lastContact || 'Just now'}</td>
                        <td style={{ padding: '12px 16px' }} onClick={() => setSelectedContact(contact)}>
                          <span style={{ padding: '4px 8px', borderRadius: '999px', backgroundColor: bg, color: color, fontSize: '11px', fontWeight: '500' }}>
                            {contact.status || 'Lead'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', position: 'relative' }}>
                          <button onClick={(e) => { e.stopPropagation(); setOpenDropdownId(openDropdownId === contact._id ? null : contact._id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="19" r="1"></circle></svg>
                          </button>
                          {openDropdownId === contact._id && (
                            <>
                              <div onClick={() => setOpenDropdownId(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }} />
                              <div style={{ position: 'absolute', top: '100%', right: '16px', marginTop: '4px', backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', padding: '4px 0', zIndex: 20, width: '120px' }}>
                                {canEditContact && (
                                  <button onClick={() => { setEditContactData(contact); setShowEditContactModal(true); setOpenDropdownId(null); }} style={{ width: '100%', textAlign: 'left', padding: '8px 16px', background: 'none', border: 'none', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>Edit Contact</button>
                                )}
                                {canDeleteContact && (
                                  <button onClick={() => { setDeleteContactId(contact._id); setShowDeleteContactModal(true); setOpenDropdownId(null); }} style={{ width: '100%', textAlign: 'left', padding: '8px 16px', background: 'none', border: 'none', fontSize: '13px', color: '#EF4444', cursor: 'pointer' }}>Delete</button>
                                )}
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div style={{ padding: '16px 24px', borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>Showing {contacts.length > 0 ? 1 : 0} to {contacts.length} of {stats.totalContacts || 0} contacts</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6B7280' }}>
                <span style={{ cursor: 'pointer' }}>&lt;</span>
                <span style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#EDE9FE', color: '#7C3AED', borderRadius: '8px', fontWeight: '500', cursor: 'pointer' }}>1</span>
                <span style={{ cursor: 'pointer' }}>&gt;</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Contact Details */}
        <div style={{ width: '320px', flexShrink: 0, backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #F3F4F6', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '20px', display: 'flex', flexDirection: 'column' }}>
          {selectedContact ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: getAvatarColors(selectedContact.status).bg, color: getAvatarColors(selectedContact.status).color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px' }}>
                    {getInitials(selectedContact.name)}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#111827' }}>{selectedContact.name || 'Unknown'}</h2>
                      <span style={{ padding: '2px 8px', borderRadius: '999px', backgroundColor: getAvatarColors(selectedContact.status).bg, color: getAvatarColors(selectedContact.status).color, fontSize: '11px', fontWeight: '500' }}>{selectedContact.status || 'Lead'}</span>
                    </div>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6B7280' }}>{selectedContact.role || 'Contact'}</p>
                  </div>
                </div>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }} onClick={() => setSelectedContact(null)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '32px' }}>
                {[
                  { label: 'Email', icon: <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path> },
                  { label: 'Call', icon: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path> },
                  { label: 'Message', icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path> },
                  { label: 'Task', icon: <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path> },
                  { label: 'More', icon: <circle cx="12" cy="12" r="1"></circle> },
                ].map((btn, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', border: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7C3AED' }}>
                      {i === 4 ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{btn.icon}</svg>}
                    </div>
                    <span style={{ fontSize: '11px', color: '#6B7280', fontWeight: '500' }}>{btn.label}</span>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: '#111827' }}>About Contact</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '12px', fontSize: '13px' }}>
                  <span style={{ color: '#6B7280' }}>Email</span><span style={{ color: '#111827', wordBreak: 'break-all' }}>{selectedContact.email || '-'}</span>
                  <span style={{ color: '#6B7280' }}>Phone</span><span style={{ color: '#111827' }}>{selectedContact.phone || '-'}</span>
                  <span style={{ color: '#6B7280' }}>Company</span><span style={{ color: '#111827' }}>{selectedContact.company || '-'}</span>
                  <span style={{ color: '#6B7280' }}>Location</span><span style={{ color: '#111827' }}>{selectedContact.location || '-'}</span>
                  <span style={{ color: '#6B7280' }}>Source</span><span style={{ color: '#111827' }}>{selectedContact.source || '-'}</span>
                  <span style={{ color: '#6B7280' }}>Owner</span><span style={{ color: '#111827' }}>{selectedContact.owner || 'Unassigned'}</span>
                </div>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111827' }}>Recent Activities</h3>
                  <span style={{ fontSize: '12px', color: '#7C3AED', fontWeight: '500', cursor: 'pointer' }}>View All</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {selectedContact.activities && selectedContact.activities.length > 0 ? selectedContact.activities.map((act, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: `${act.color || '#7C3AED'}15`, color: act.color || '#7C3AED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <circle cx="12" cy="12" r="5" fill="currentColor"/>
                        </div>
                        <span style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>{act.title}</span>
                      </div>
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>{act.date}</span>
                    </div>
                  )) : (
                    <div style={{ fontSize: '12px', color: '#9CA3AF' }}>No recent activities.</div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px' }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              <p style={{ fontSize: '14px', margin: 0 }}>Select a contact to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Contact Modal */}
      {showAddContactModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', width: '500px', maxWidth: '90%', padding: '24px', position: 'relative', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>Add New Contact</h3>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Name *</label>
                <input type="text" value={newContact.name} onChange={e => setNewContact({...newContact, name: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Email *</label>
                <input type="email" value={newContact.email} onChange={e => setNewContact({...newContact, email: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Phone</label>
                <input type="text" value={newContact.phone} onChange={e => setNewContact({...newContact, phone: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Company</label>
                  <input type="text" value={newContact.company} onChange={e => setNewContact({...newContact, company: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Role</label>
                  <input type="text" value={newContact.role} onChange={e => setNewContact({...newContact, role: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Status</label>
                <select value={newContact.status} onChange={e => setNewContact({...newContact, status: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }}>
                  <option value="Lead">Lead</option>
                  <option value="Prospect">Prospect</option>
                  <option value="Customer">Customer</option>
                  <option value="Vendor">Vendor</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button onClick={() => setShowAddContactModal(false)} style={{ padding: '8px 20px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveContact} disabled={isSavingContact} style={{ padding: '8px 20px', backgroundColor: '#7C3AED', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#fff', cursor: isSavingContact ? 'not-allowed' : 'pointer', opacity: isSavingContact ? 0.7 : 1 }}>
                {isSavingContact ? 'Saving...' : 'Save Contact'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {showEditContactModal && editContactData && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', width: '500px', maxWidth: '90%', padding: '24px', position: 'relative', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>Edit Contact</h3>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Name *</label>
                <input type="text" value={editContactData.name || ''} onChange={e => setEditContactData({...editContactData, name: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Email *</label>
                <input type="email" value={editContactData.email || ''} onChange={e => setEditContactData({...editContactData, email: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Phone</label>
                <input type="text" value={editContactData.phone || ''} onChange={e => setEditContactData({...editContactData, phone: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Company</label>
                  <input type="text" value={editContactData.company || ''} onChange={e => setEditContactData({...editContactData, company: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Role</label>
                  <input type="text" value={editContactData.role || ''} onChange={e => setEditContactData({...editContactData, role: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Status</label>
                <select value={editContactData.status || 'Lead'} onChange={e => setEditContactData({...editContactData, status: e.target.value})} style={{ width: '100%', padding: '10px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', boxSizing: 'border-box' }}>
                  <option value="Lead">Lead</option>
                  <option value="Prospect">Prospect</option>
                  <option value="Customer">Customer</option>
                  <option value="Vendor">Vendor</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button onClick={() => setShowEditContactModal(false)} style={{ padding: '8px 20px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleUpdateContact} disabled={isSavingEdit} style={{ padding: '8px 20px', backgroundColor: '#7C3AED', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#fff', cursor: isSavingEdit ? 'not-allowed' : 'pointer', opacity: isSavingEdit ? 0.7 : 1 }}>
                {isSavingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteContactModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', width: '400px', maxWidth: '90%', padding: '24px', position: 'relative', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#FEF2F2', color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>Delete Contact?</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#6B7280' }}>Are you sure you want to delete this contact? This action cannot be undone.</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button onClick={() => setShowDeleteContactModal(false)} style={{ padding: '8px 20px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeleteContact} disabled={isDeletingContact} style={{ padding: '8px 20px', backgroundColor: '#EF4444', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#fff', cursor: isDeletingContact ? 'not-allowed' : 'pointer', opacity: isDeletingContact ? 0.7 : 1 }}>
                {isDeletingContact ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}